import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AgentAuditOperationsTab from '../Components/AgentAuditOperationsTab';
import AgentInstructionsTab from '../Components/AgentInstructionsTab';
import AgentMetricsTab from '../Components/AgentMetrics/AgentMetricsTab';
import AgentNotesTab from '../Components/AgentNotesTab';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';
import { Tab, Tabs } from '../Components/Tabs';
import TestAgentModal from '../Components/TestAgentModal';

import AgentSettings from '../Components/AgentSettings';
import { FetchItemsParams, FetchItemsResult } from '../Components/ItemsPickerModalTypes';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import { AgentApiProvider, useAgentApi } from '../Services/AgentApiProvider';
import { AssistantNoteApiProvider } from '../Services/AssistantNoteApiProvider';

import { useAuth } from '../Contexts/AuthContext';
import { AssistantFrameApi, AssistantSwarmApi } from '../Services/AgentApiProviderTypes';
import { ArtifactApiProvider, useArtifactApi } from '../Services/ArtifactApiProvider';
import { isEmpty } from '../utils/validationUtil';
import {
  AGENT_TAB_LABELS,
  AGENT_TABS,
  AgentConfig,
  AgentFeatureFlags,
  AgentPermissions,
  AgentReferenceData,
  AgentTab,
  agentTabPath,
  configToPayload,
  DEFAULT_PRESETS,
  EMPTY_DATA,
  getCollectionDisableReason,
  INITIAL_CONFIG,
  isAgentTab,
} from './AgentHelpers';

function AgentContent({ canEdit }: { canEdit: boolean }) {
  const navigate = useNavigate();
  const { organizationId, agentId } = useParams<{ organizationId: string; agentId: string }>();
  const { showAlert } = useAlert();
  const { status } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isDocumentPermission = status?.permissions?.includes('all:document') ?? false;
  const isOrganizationsPermission = status?.permissions?.includes('all:organizations') ?? false;
  const isNew = agentId === 'new-agent';
  const isDisabled = !canEdit;

  const {
    getAgents,
    getAgentById,
    getAgentNotifications,
    getAgentCatalog,
    getAgentFrames,
    getAgentMarkers,
    getAgentInlineSkills,
    getAgentPrompts,
    createAgent,
    updateAgentById,
    getPrompts,
    getNotifications,
    getCatalogs,
    getFramesPage,
    getMarkers,
    getInlineSkills,
    getParameterizedFragments,
    getDictionaries,
    getAllSwarm,
    getAgentSwarm,
    getDocumentCollections,
    getInformationPackageCollections,
    getConnections,
    getAssistantOptions,
  } = useAgentApi();

  const { getArtifact } = useArtifactApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Agent config (single object instead of 25 separate useState) ─

  const [config, setConfig] = useState<AgentConfig>(INITIAL_CONFIG);

  const updateConfig = useCallback(<K extends keyof AgentConfig>(key: K, value: AgentConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Reference / lookup data ───────────────────────────────────

  const [refData, setRefData] = useState<AgentReferenceData>(EMPTY_DATA);

  // ── Derived state (replaces 3 of the 4 original useEffects) ───

  const collectionDisableReason = useMemo(
    () => getCollectionDisableReason(config.agentType, config.skills, config.reasoning),
    [config.agentType, config.skills, config.reasoning],
  );
  const disableCollections = !!collectionDisableReason;
  const collectionTextWarning = collectionDisableReason ?? '';

  const isPromptRealtime = useMemo(() => {
    if (!config.promptIds.length || !refData.prompts.length) return false;
    return refData.prompts.some(
      (p) => config.promptIds.includes(p.id) && p.pattern_type === 'realtime',
    );
  }, [config.promptIds, refData.prompts]);

  // Clear collections when they become disabled (keeps the original side-effect)
  useEffect(() => {
    if (disableCollections) {
      setConfig((prev) =>
        prev.documentCollectionIds.length > 0 ? { ...prev, documentCollectionIds: [] } : prev,
      );
    }
  }, [disableCollections]);

  const permissions: AgentPermissions = {
    isDisabled,
    isOrganizationsPermission,
    isDocumentPermission,
    disableCollections,
    collectionTextWarning,
  };

  const featureFlags: AgentFeatureFlags = {
    isDocumentFlag: status?.feature_flags?.DOCUMENT === true,
    isProductFlag: status?.feature_flags?.PRODUCT === true,
    isInformationPackageFlag: status?.feature_flags?.INFORMATION_PACKAGE === true,
    isNotificationFlag: status?.feature_flags?.NOTIFICATION === true,
    isConnectionFlag: status?.feature_flags?.CONNECTION === true,
  };

  // ── Frames picker paging ──────────────────────────────────────
  // ItemsPickerModal pages with an opaque cursor; the frames endpoint uses
  // skip/limit, so the cursor is just the next skip offset.
  const fetchFrames = useCallback(
    async ({
      limit,
      pageToken,
      searchQuery,
    }: FetchItemsParams): Promise<FetchItemsResult<AssistantFrameApi>> => {
      const skip = pageToken ? Number(pageToken) : 0;
      const response = await getFramesPage(skip, limit, searchQuery);
      const items = response.items ?? [];
      const nextSkip = skip + items.length;
      const hasMore = items.length > 0 && nextSkip < (response.totalCount ?? 0);
      return {
        items,
        nextPageToken: hasMore ? String(nextSkip) : null,
        hasMore,
        totalCount: response.totalCount ?? undefined,
      };
    },
    [getFramesPage],
  );

  // ── Data fetch ────────────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // The agent's own frames double as the picker's pre-selected items, so
        // a selection is never lost just because its row sits on a later page.
        let agentFrames: AssistantFrameApi[] = [];

        if (!isNew && agentId) {
          const [agent, notifs, frames, markers, inlineSkills, agentPrompts, swarms, catalog] =
            await Promise.all([
              getAgentById(agentId),
              getAgentNotifications(agentId),
              getAgentFrames(agentId),
              getAgentMarkers(agentId),
              getAgentInlineSkills(agentId),
              getAgentPrompts(agentId),
              getAgentSwarm(agentId),
              isDocumentPermission ? getAgentCatalog(agentId) : Promise.resolve(null),
            ]);

          agentFrames = frames ?? [];

          setConfig({
            name: agent.name,
            isActive: agent.is_active,
            agentType: agent.assistant_type,
            modelName: agent.model_name || null,
            reasoning: agent.reasoning || null,
            serviceTier: agent.service_tier || null,
            temperature: agent.temperature,
            detailsLevel: agent.details_level,
            presets: agent.presets ?? DEFAULT_PRESETS,
            promptIds: agentPrompts?.map((p) => p.id) ?? [],
            noFramePromptIds: agent.no_frame_prompt_keys ?? [],
            selectedVoice: agent.voice ?? 'cedar',
            memoryType: agent.memory_type,
            memoryLimit: agent.memory_limit,
            skills: agent.skills ?? [],
            frameIds: frames?.map((f: { id: string }) => f.id) ?? [],
            markerIds: markers?.map((m: { id: string }) => m.id) ?? [],
            inlineSkillKeys: inlineSkills?.map((inlineSkill) => inlineSkill.key) ?? [],
            fragmentKeys: agent.fragment_keys ?? [],
            swarmKeys: swarms?.map((s: { key: string }) => s.key) ?? [],
            notificationIds: notifs?.map((n: { id: string }) => n.id) ?? [],
            documentCollectionIds: agent.document_collection_keys ?? [],
            informationPackageCollectionIds: agent.information_package_collection_keys ?? [],
            connectionIds: agent.connections ?? [],
            catalog: catalog?.id ?? null,
            dictionaryId: agent.taxonomy_dictionary_id ?? null,
            templateIds: agent.templates ?? [],
            auditExecution: agent.audit_execution ?? true,
            nextAgentKey: agent.next_assistant_key ?? null,
          });
        }

        const [
          prompts,
          notifications,
          documentCollections,
          informationPackageCollections,
          connections,
          allMarkers,
          allInlineSkills,
          allFragments,
          dictionaries,
          allSwarms,
          artifactData,
          catalogs,
          options,
          allAgents,
        ] = await Promise.all([
          getPrompts(),
          getNotifications(),
          getDocumentCollections(),
          getInformationPackageCollections(),
          getConnections(),
          getMarkers(),
          getInlineSkills(),
          getParameterizedFragments(),
          getDictionaries(),
          getAllSwarm().then((s: AssistantSwarmApi[]) => s || []),
          getArtifact(0, 1000, 'template'),
          isDocumentPermission ? getCatalogs() : Promise.resolve([]),
          getAssistantOptions(),
          getAgents(0, 100),
        ]);

        setRefData({
          prompts,
          notifications,
          documentCollections,
          informationPackageCollections,
          connections,
          selectedFrames: agentFrames,
          markers: allMarkers,
          inlineSkills: allInlineSkills,
          fragments: allFragments,
          dictionaries,
          swarms: allSwarms,
          templates: artifactData.items,
          catalogs,
          // An agent can't be chained to itself.
          agents: (allAgents.items ?? []).filter((a) => a.key !== agentId),
          options,
        });
      } catch (err) {
        console.error('Error fetching agent data:', err);
        setError('Failed to load agent data');
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, isDocumentPermission]);

  // ── Save ──────────────────────────────────────────────────────

  const onSave = async () => {
    if (isEmpty(config.name)) {
      showAlert('fail', 'Please enter agent name');
      return;
    }
    if (config.presets.includes('DETAILS_LEVEL') && isEmpty(config.detailsLevel)) {
      showAlert('fail', 'Please select level of details');
      return;
    }
    if (config.promptIds.length === 0) {
      showAlert('fail', 'Please select prompt');
      return;
    }
    if (isEmpty(config.agentType)) {
      showAlert('fail', 'Please select agent type');
      return;
    }
    if (disableCollections && config.documentCollectionIds?.length > 0) {
      showAlert('fail', `Cannot save agent: ${collectionTextWarning}`);
      return;
    }
    if (
      config.agentType === 'RAG' &&
      config.documentCollectionIds?.length > 0 &&
      isEmpty(config.reasoning)
    ) {
      showAlert('fail', 'Please select reasoning');
      return;
    }

    setLoading(true);
    try {
      const payload = configToPayload(config);

      if (isNew) {
        const result = await createAgent(payload);
        showAlert('success', 'Agent created successfully');
        navigate(`/organizations/${organizationId}/agents-hub/${result.key}`, {
          state: { organizationId },
        });
      } else {
        await updateAgentById(agentId, payload);
        showAlert('success', 'Agent updated successfully');
      }
    } catch (err) {
      console.error('Error saving agent data:', err);
      showAlert('fail', 'Failed to save agent data');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/agents-hub`, { state: { organizationId } });
  };

  if (loading) return <PageLoader />;

  const requestedTab = searchParams.get('tab');
  const activeTab = isAgentTab(requestedTab) ? requestedTab : AGENT_TABS.SETTINGS;

  const handleTabChange = (value: string): void => {
    const nextParams = new URLSearchParams(searchParams);
    if (value === AGENT_TABS.SETTINGS) nextParams.delete('tab');
    else nextParams.set('tab', value);
    setSearchParams(nextParams, { replace: true });
  };

  const testAgentPanel = (
    <div className="radius-yes p-2 bg-yes radius col-6">
      <h2 className="h2-title">Test Agent</h2>
      <div className="gap d-flex border-yes radius-yes flex-row-to-col flex-column border-yes">
        <TestAgentModal isOpen isPromptRealtime={isPromptRealtime} />
      </div>
    </div>
  );

  const settingsTab = (
    <div className="row d-flex gap">
      <div className="radius-yes p-2 bg-yes col-6">
        <div className="d-flex justify-between align-center wrap gap">
          <h2 className="h2-title m-0" style={{ flex: '1 1 200px', minWidth: '200px' }}>
            {config.name}
          </h2>
        </div>

        <div className="gap d-flex flex-row-to-col flex-column">
          <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
            <AgentSettings
              config={config}
              data={refData}
              permissions={permissions}
              featureFlags={featureFlags}
              onChange={updateConfig}
              fetchFrames={fetchFrames}
            />
          </div>
        </div>

        <div className="buttons-container d-flex justify-end">
          <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
            Save
          </Button>
          <Button variant="danger" size="medium" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>

      {!isNew && testAgentPanel}
    </div>
  );

  return (
    <div className="container">
      <Breadcrumbs />
      {isNew ? (
        settingsTab
      ) : (
        <Tabs activeTab={activeTab} onTabChange={handleTabChange}>
          <Tab label={AGENT_TAB_LABELS.settings} value={AGENT_TABS.SETTINGS} wrapText={false}>
            {settingsTab}
          </Tab>
          <Tab label={AGENT_TAB_LABELS.notes} value={AGENT_TABS.NOTES} wrapText={false}>
            <div className="row d-flex gap">
              <AgentNotesTab agentId={agentId} canEdit={canEdit} />
            </div>
          </Tab>
          <Tab
            label={AGENT_TAB_LABELS.instructions}
            value={AGENT_TABS.INSTRUCTIONS}
            wrapText={false}
          >
            <div className="row d-flex gap">
              {/* Read-only, as on the instructions page this tab replaces */}
              <AgentInstructionsTab agentId={agentId} canEdit={false} />
              {testAgentPanel}
            </div>
          </Tab>
          <Tab label={AGENT_TAB_LABELS.audit} value={AGENT_TABS.AUDIT} wrapText={false}>
            <div className="row d-flex gap">
              <AgentAuditOperationsTab agentId={agentId} />
              {testAgentPanel}
            </div>
          </Tab>
          <Tab label={AGENT_TAB_LABELS.metrics} value={AGENT_TABS.METRICS} wrapText={false}>
            <div className="row d-flex gap w-100">
              <div className="radius-yes p-2 bg-yes col-12">
                <AgentMetricsTab agentId={agentId} />
              </div>
            </div>
          </Tab>
        </Tabs>
      )}
    </div>
  );
}

// The notes, instructions and audit operations pages became tabs; old links open the matching tab
export function AgentTabRedirect({ tab }: { tab: AgentTab }) {
  const { organizationId, agentId } = useParams<{ organizationId: string; agentId: string }>();

  return <Navigate to={agentTabPath(organizationId, agentId, tab)} replace />;
}

function Agent({ canEdit }: { canEdit: boolean }) {
  return (
    <ConfirmationProvider>
      <AgentApiProvider>
        <ArtifactApiProvider>
          <AssistantNoteApiProvider>
            <AgentContent canEdit={canEdit} />
          </AssistantNoteApiProvider>
        </ArtifactApiProvider>
      </AgentApiProvider>
    </ConfirmationProvider>
  );
}

export default Agent;
